import {IQuotationRow, IRemarkItem} from "./quotationListTypes";
import {toTitleCase} from "../../util/textcase";
import {GenericQuotationStatusItem, IQuotationListItemModel} from "../../models/axiosModel";
import moment from "moment";
import {
    findSavedFollowUpChange,
    normalizeFollowUps,
    shouldTransitionSentToFollowUpAfterFollowUpSave,
} from "../quotationStatusBadge/activityHistoryUtil";
import { resolveQuotationListNextFollowUpDate } from "./quotationListStatusMetaUtil";
import type { IFollowUpItem } from "./quotationListTypes";

export const formatPrice = (value: number) => {
    return Number.isFinite(value) ? value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : "0"
}

const QUOTATION_SOURCE_OTHER_TYPE_ID = 4

const isQuotationListSourceOther = (
    typeId?: number | null,
    typeName?: string | null,
): boolean =>
    Number(typeId) === QUOTATION_SOURCE_OTHER_TYPE_ID
    || String(typeName || "").toLowerCase().trim() === "other"

export const resolveQuotationListSource = (
    typeId?: number | null,
    typeName?: string | null,
    sourceOther?: string | null,
): string => {
    if (isQuotationListSourceOther(typeId, typeName)) {
        return String(sourceOther || "").trim()
    }
    return String(typeName || "").trim()
}

export const formatQuotationListTravelDates = (startDate?: string, endDate?: string): string => {
    const start = moment(startDate)
    const end = moment(endDate)
    if (!start.isValid() || !end.isValid()) return "-"
    if (start.year() === end.year()) {
        return `${start.format("DD MMM")} - ${end.format("DD MMM YYYY")}`
    }
    return `${start.format("DD MMM YYYY")} - ${end.format("DD MMM YYYY")}`
}

/** Set true to restore title-casing of list `quotation_id`. Preview version actions need the raw API id. */
const TITLE_CASE_QUOTATION_LIST_ID = false

export const getGenericQuotationStatusRoot = (genericResponse: unknown): { quotation_status?: GenericQuotationStatusItem[] } | null => {
    const data = (genericResponse as { data?: unknown } | null)?.data
    if (Array.isArray(data)) {
        const first = data[0] as { quotation_status?: GenericQuotationStatusItem[]; quotation_status_id?: number } | undefined
        if (Array.isArray(first?.quotation_status)) return first
        if (first?.quotation_status_id != null) return { quotation_status: data as GenericQuotationStatusItem[] }
        return null
    }
    if (data && Array.isArray((data as { quotation_status?: GenericQuotationStatusItem[] }).quotation_status)) {
        return data as { quotation_status?: GenericQuotationStatusItem[] }
    }
    return null
}

export const buildQuotationStatusNameById = (genericRoot: unknown): Map<number, string> => {
    const root = genericRoot as { quotation_status?: GenericQuotationStatusItem[] } | null
    const list = Array.isArray(root?.quotation_status) ? root.quotation_status : []
    const map = new Map<number, string>()
    list.forEach((item) => {
        const id = Number(item?.quotation_status_id)
        const name = String(item?.quotation_status_name || "").trim()
        if (id > 0 && name) map.set(id, name)
    })
    return map
}

export const buildQuotationStatusFilterOptions = (
    genericRoot: unknown,
): { label: string; value: string }[] => {
    const root = genericRoot as { quotation_status?: GenericQuotationStatusItem[] } | null
    const list = Array.isArray(root?.quotation_status) ? [...root.quotation_status] : []
    return list
        .sort((a, b) => Number(a.quotation_status_id) - Number(b?.quotation_status_id))
        .map((item) => ({
            label: String(item?.quotation_status_name || "").trim(),
            value: String(Number(item?.quotation_status_id) || ""),
        }))
        .filter((option) => option.label && option.value)
}

export const resolveListQuotationStatusId = (item: IQuotationListItemModel): number => {
    const raw = item as IQuotationListItemModel & {
        quotation_status?: number
        quotation_status_id?: number
    }
    return Number(raw.quotation_status ?? raw.quotation_status_id ?? raw.status_id ?? 0) || 0
}

const resolveQuotationListStatusName = (
    item: IQuotationListItemModel,
    statusNameById?: Map<number, string>,
): string => {
    const statusId = resolveListQuotationStatusId(item)
    const fromGeneric = statusId > 0 ? statusNameById?.get(statusId) : undefined
    const fromList = String(
        (item as { quotation_status_name?: string }).quotation_status_name || item.status_name || "",
    ).trim()
    return String(fromGeneric || fromList || "").trim() || "Draft"
}

export const mapQuotationListItemToRow = (
    item: IQuotationListItemModel,
    statusNameById?: Map<number, string>,
): IQuotationRow => ({
    sta_code: toTitleCase(item.sta_code),
    quotation_id: TITLE_CASE_QUOTATION_LIST_ID ? toTitleCase(item.quotation_id) : item.quotation_id,
    source: resolveQuotationListSource(
        item.quotation_source_type_id,
        item.quotation_source_type_name,
        item.quotation_source_other,
    ),
    quotation_name: toTitleCase(item.quotation_name),
    agent_name: toTitleCase(item.agent_name),
    agency_name: toTitleCase(item.agency_name),
    contact_info: {
        phone: String((item as any).agent_contact_number || "").trim(),
        email: String((item as any).agent_email || "").trim(),
    },
    start_date: item.start_date,
    end_date: item.end_date,
    cities_covered: toTitleCase(item.cities_covered?.join(", ")) || "",
    total_pax: item.total_pax,
    total_price_usd: Number(item.total_price_usd || 0),
    status_id: resolveListQuotationStatusId(item),
    status: resolveQuotationListStatusName(item, statusNameById),
    created_by: toTitleCase(item.created_by),
    created_at: item.created_at ?? null,
    ready_to_send_at: item.ready_to_send_at ?? null,
    quotation_downloads: Array.isArray(item.quotation_downloads) ? item.quotation_downloads : [],
    follow_ups: normalizeFollowUps(item.quotation_follow_ups),
    quotation_status_changed_at: String(
        (item as { quotation_status_changed_at?: string | null }).quotation_status_changed_at || "",
    ).trim() || null,
    next_follow_up_date: resolveQuotationListNextFollowUpDate(
        normalizeFollowUps(item.quotation_follow_ups),
        (item as { next_follow_up_date?: string | null }).next_follow_up_date ?? null,
    ),
    remark: String(
        (item).remark
        ?? "",
    ).trim() || null,
    quotation_status_id: item.quotation_status_id,
    number_of_children: item.number_of_children,
    flag_with_tip: item.flag_with_tip,
    agency_id: item.agency_id,
    flag_all_days_have_dinner: item.flag_all_days_have_dinner,
    foc_pax_number: item.foc_pax_number,
    agent_id: item.agent_id,
    quotation_status_name: item.quotation_status_name,
    number_of_adults: item.number_of_adults,
    number_of_extra_bed: item.number_of_extra_bed
})

/** List Duplicate: Ready to Send, Confirmed, Sent, Follow Up. */
export const QUOTATION_LIST_DUPLICATE_STATUS_IDS = [2, 3, 4, 5] as const

export const QUOTATION_LIST_MARK_CONFIRMED_LOST_STATUS_IDS = [3, 4] as const

export const QUOTATION_LIST_DELETE_DRAFT_STATUS_ID = 1 as const

export const quotationStatusIdByName = (
    genericRoot: unknown,
    name: string,
): number => {
    const root = genericRoot as { quotation_status?: GenericQuotationStatusItem[] } | null
    const list = Array.isArray(root?.quotation_status) ? root.quotation_status : []
    const item = list.find(
        (status) => String(status?.quotation_status_name || "").toLowerCase().trim() === name.toLowerCase().trim(),
    )
    return Number(item?.quotation_status_id) || 0
}

export const buildQuotationListMarkConfirmedLostStatusIds = (genericRoot: unknown): number[] => {
    const sentStatusId = quotationStatusIdByName(genericRoot, "sent")
    const followUpStatusId = quotationStatusIdByName(genericRoot, "follow up")
    const ids = [sentStatusId, followUpStatusId].filter((id) => id > 0)
    return ids.length > 0 ? ids : [...QUOTATION_LIST_MARK_CONFIRMED_LOST_STATUS_IDS]
}

export const buildQuotationListDraftStatusId = (genericRoot: unknown): number => {
    const draftStatusId = quotationStatusIdByName(genericRoot, "draft")
    return draftStatusId > 0 ? draftStatusId : QUOTATION_LIST_DELETE_DRAFT_STATUS_ID
}

export const canDuplicateQuotationListRow = (row: Pick<IQuotationRow, "status_id">): boolean =>
    QUOTATION_LIST_DUPLICATE_STATUS_IDS.includes(row.status_id as (typeof QUOTATION_LIST_DUPLICATE_STATUS_IDS)[number])

export const canMarkConfirmedOrLostQuotationListRow = (
    row: Pick<IQuotationRow, "status_id">,
    markStatusIds: number[] = [...QUOTATION_LIST_MARK_CONFIRMED_LOST_STATUS_IDS],
): boolean => markStatusIds.includes(row.status_id)

export const canDeleteQuotationListRow = (
    row: Pick<IQuotationRow, "status_id">,
    draftStatusId: number = QUOTATION_LIST_DELETE_DRAFT_STATUS_ID,
): boolean => row.status_id === draftStatusId

export const normalizeRemarks = (raw: any[]): IRemarkItem[] => {
    if (!Array.isArray(raw)) return []
    return raw
        .map((item, index) => ({
            quotation_remark_id: item?.quotation_remark_id ? String(item.quotation_remark_id) : null,
            remark: String(item?.remark || "").trim(),
            sequence_number: Number(item?.sequence_number ?? index + 1),
            created_at: item?.created_at ?? item?.created_at_timestamp ?? null,
            created_by: item?.created_by ?? null,
            updated_at: item?.updated_at ?? item?.updated_at_timestamp ?? null,
            updated_by: item?.updated_by ?? null,
        }))
        .filter((item) => item.remark.length > 0)
}

export const getRemarksFromDetailsResponse = (response: any): IRemarkItem[] => {
    const data = response?.data
    const topLevelRemarks = data?.remarks
    const quotationLevelRemarks = data?.quotation?.remarks
    const raw = Array.isArray(topLevelRemarks) ? topLevelRemarks : Array.isArray(quotationLevelRemarks) ? quotationLevelRemarks : []
    return normalizeRemarks(raw)
}

export const formatRemarkDate = (value?: string | number | null) => {
    if (value == null || value === "") return "-"
    const m = moment(value)
    if (!m.isValid()) return String(value)
    return m.format("YYYY-MM-DD HH:mm")
}

const normalizeQuotationStatusName = (value: string): string =>
    value.trim().toLowerCase().replace(/\s+/g, " ")

export const applyQuotationListRowFollowUpsChange = (
    row: IQuotationRow,
    nextFollowUps: IFollowUpItem[],
    quotationStatusOptions: GenericQuotationStatusItem[] = [],
): IQuotationRow => {
    const nextFollowUpDate = resolveQuotationListNextFollowUpDate(nextFollowUps)
    const savedFollowUp = findSavedFollowUpChange(row.follow_ups, nextFollowUps)

    if (
        !savedFollowUp
        || !shouldTransitionSentToFollowUpAfterFollowUpSave(
            row.status_id,
            row.status,
            savedFollowUp,
            quotationStatusOptions,
        )
    ) {
        return {
            ...row,
            follow_ups: nextFollowUps,
            next_follow_up_date: nextFollowUpDate,
        }
    }

    const followUpStatus = quotationStatusOptions.find(
        (item) => normalizeQuotationStatusName(String(item.quotation_status_name ?? "")) === "follow up",
    )
    const nextStatusId = Number(followUpStatus?.quotation_status_id) || row.status_id
    const nextStatusName = String(followUpStatus?.quotation_status_name || "Follow Up").trim() || row.status

    return {
        ...row,
        follow_ups: nextFollowUps,
        next_follow_up_date: nextFollowUpDate,
        status_id: nextStatusId,
        status: nextStatusName,
        quotation_status_id: nextStatusId,
        quotation_status_name: nextStatusName,
    }
}